/**
 * How a payout statement explains its fee (COL-609).
 *
 * The statement used to divide the fee by the rent and print "3,7 % der
 * Nettokaltmiete" — wrong for every flat-fee-per-room contract, where the fee
 * is a fixed amount per room and the percentage is only the rent level showing
 * through. The real basis is the invoice's own fee_basis snapshot, which
 * owner_payout_statements() now returns as `fee_lines`.
 *
 * This module only DESCRIBES the recorded fee; it never produces one. A flat
 * description is returned only when rooms × rate adds up to the recorded fee to
 * the cent. A fee the live-fee rule capped, or one an admin typed over, does
 * not foot, and printing the arithmetic anyway would put a sum on an owner's
 * statement that is not the sum — those fall back to a plain fee line.
 *
 * Mirrored by supabase/functions/_shared/payout-fee.ts, which the PDF renders
 * from (the edge function cannot import from src); the two are held together
 * by owner-payout-fee.parity.test.ts. Change both or neither.
 */

export type PayoutFeeLine = {
  revenue_model: string | null;
  /** Rooms the fee was billed on: LEAST(paid, occupied). */
  rooms: number | null;
  rooms_occupied: number | null;
  rate_eur: number | null;
  pct: number | null;
  fee_eur: number | null;
};

export type PayoutFeeRow = {
  rent_collected_cents: number;
  fee_cents: number;
  fee_lines: PayoutFeeLine[] | null;
};

export type FeeGroup = { rooms: number; rateCents: number };

export type FeeExplanation =
  | { kind: "waived" }
  | { kind: "flat"; groups: FeeGroup[] }
  | { kind: "pct"; pct: number }
  /** No basis we can state truthfully: print the fee, claim nothing about it. */
  | { kind: "plain" };

const isPositive = (n: number | null | undefined): n is number =>
  typeof n === "number" && Number.isFinite(n) && n > 0;

export function explainFee(row: PayoutFeeRow): FeeExplanation {
  // A referral free month is "gebührenfrei", not "0 % der Nettokaltmiete".
  if (row.fee_cents <= 0) return { kind: "waived" };

  // Units that billed nothing (a unit with no paid room) are not part of the
  // explanation.
  const lines = (row.fee_lines ?? []).filter((l) => isPositive(l.fee_eur));
  if (lines.length === 0) return { kind: "plain" };

  if (lines.every((l) => l.revenue_model === "flat_fee_per_room")) {
    const byRate = new Map<number, number>();
    let totalCents = 0;
    for (const l of lines) {
      if (
        !isPositive(l.rooms) ||
        !Number.isInteger(l.rooms) ||
        !isPositive(l.rate_eur)
      ) {
        return { kind: "plain" };
      }
      const rateCents = Math.round(l.rate_eur * 100);
      totalCents += rateCents * l.rooms;
      byRate.set(rateCents, (byRate.get(rateCents) ?? 0) + l.rooms);
    }
    if (totalCents !== row.fee_cents) return { kind: "plain" };
    return {
      kind: "flat",
      groups: [...byRate.entries()].map(([rateCents, rooms]) => ({
        rooms,
        rateCents,
      })),
    };
  }

  if (lines.every((l) => l.revenue_model === "pct_of_rent")) {
    const pcts = new Set(lines.map((l) => l.pct));
    const [only] = [...pcts];
    if (pcts.size === 1 && isPositive(only)) {
      // The same foot-check as the flat branch: the live-fee rule caps a fee
      // at the month's roll fee, and an admin can type over it, so a recorded
      // 12 % beside a fee that is not 12 % of the rent would be a claim the
      // numbers contradict. One cent of rounding per unit is allowed.
      const expected = Math.round((row.rent_collected_cents * only) / 100);
      if (Math.abs(expected - row.fee_cents) <= lines.length) {
        return { kind: "pct", pct: only };
      }
    }
  }

  return { kind: "plain" };
}

/**
 * "33 €" for a whole-euro rate, "33,50 €" otherwise. The portal's money
 * formatter rounds to whole euros, which is right for totals and wrong for a
 * per-room rate: €33.50 must not read €34.
 */
export function formatRateCents(cents: number, locale: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}
